import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/portal/Shared";
import { toast } from "sonner";
import {
  Search, Sparkles, ShieldAlert, AlertTriangle, Loader2, BookmarkPlus, Trash2, ExternalLink,
  Leaf, GitCompare, Phone, ScrollText, Landmark, UtensilsCrossed, Languages, ChevronRight,
} from "lucide-react";

const field = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";
const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const PERIODS = ["All Periods", "Pre-contact / Ancestral", "1700s–1800s", "1800s–1900s", "Contemporary documentation"];

const EVIDENCE_STYLE = {
  "Tribal Source": "bg-[#E7F6EC] text-[#166534]",
  "Historical Source": "bg-[#FCF3E4] text-[#8F7220]",
  "Ethnobotanical Source": "bg-[#F3EEFA] text-[#59288E]",
};

function Disclaimer() {
  return (
    <div className="rounded-xl border border-[#D4AF37]/40 bg-[#FCF8EE] p-4 mb-5" data-testid="wellness-disclaimer">
      <div className="flex items-center gap-2 text-[#8F7220] text-xs font-bold uppercase tracking-wide mb-1"><ShieldAlert size={15} /> Educational &amp; Cultural Information</div>
      <p className="text-sm text-[#5a5460] leading-relaxed">WhatsMyTribe.com provides historical, cultural, and educational information concerning Indigenous plant knowledge. Traditional use does not establish that a plant is safe or effective for treating a medical condition. This tool does not diagnose conditions, prescribe treatments, or replace qualified medical care. Plants may cause adverse effects or interact with medications, supplements, pregnancy, or existing health conditions.</p>
    </div>
  );
}

function SafetyBlock({ s }) {
  return (
    <div className="mt-3 rounded-lg border border-[#E4DCD3] bg-[#FBFAF8] p-3">
      {s.high_risk && (
        <div className="rounded-md bg-[#FDECEC] border border-[#E5484D]/40 p-2.5 mb-2" data-testid="safety-warning">
          <div className="flex items-center gap-1.5 text-[#B42318] text-xs font-bold uppercase"><AlertTriangle size={14} /> Safety warning</div>
          <p className="text-xs text-[#7A271A] mt-1">Historical use does not mean this plant is safe for modern self-treatment. No dosage guidance is provided for toxic or potentially dangerous plants.</p>
        </div>
      )}
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220] mb-1">Modern safety</div>
      <ul className="text-xs text-[#5a5460] space-y-1">
        <li><span className="font-semibold">Toxicity:</span> {s.toxicity_status}</li>
        <li><span className="font-semibold">Interactions:</span> {s.known_interactions}</li>
        <li><span className="font-semibold">Pregnancy:</span> {s.pregnancy_warning}</li>
        <li><span className="font-semibold">Children:</span> {s.child_warning}</li>
        <li><span className="font-semibold">Identification:</span> {s.identification_risk}</li>
      </ul>
    </div>
  );
}

function PlantCard({ p }) {
  return (
    <div className="rounded-2xl border border-[#E4DCD3] bg-white overflow-hidden" data-testid={`wellness-plant-${p.id}`}>
      {p.image_url ? <div className="h-40 overflow-hidden"><img src={IMG_BASE + p.image_url} alt={p.common_name} className="h-full w-full object-cover" loading="lazy" /></div> : null}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${EVIDENCE_STYLE[p.evidence_classification] || "bg-gray-100 text-gray-600"}`}>{p.evidence_classification}</span>
          {p.safety.high_risk ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#B42318]"><AlertTriangle size={12} /> High caution</span> : null}
        </div>
        <h3 className="font-serif text-xl font-semibold text-[#180D24] mt-2">{p.common_name}</h3>
        <div className="text-xs text-[#8F7220] font-mono italic">{p.scientific_name}{p.native_name ? ` · ${p.native_name}` : ""}</div>
        <dl className="mt-3 space-y-1.5 text-sm">
          <div><dt className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">Plant part historically recorded</dt><dd className="text-[#1A171D]">{p.plant_part}</dd></div>
          <div><dt className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">Recorded traditional association</dt><dd className="text-[#1A171D]">{p.recorded_association}</dd></div>
          {p.historical_preparation ? <div><dt className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">One recorded preparation</dt><dd className="text-[#1A171D]">{p.historical_preparation}</dd></div> : null}
          <div><dt className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">Documented among</dt><dd className="text-[#1A171D]">{p.tribal_uses.map((t) => t.nation).join(", ")}</dd></div>
        </dl>
        <SafetyBlock s={p.safety} />
        <div className="mt-2 text-[11px] text-[#756D7E]"><span className="font-semibold">Source:</span> {p.source || "—"}</div>
      </div>
    </div>
  );
}

export default function WellnessAssistant({ meta, plants, onNavigate }) {
  const [topic, setTopic] = useState("");
  const [nation, setNation] = useState("");
  const [region, setRegion] = useState("");
  const [plant, setPlant] = useState("");
  const [period, setPeriod] = useState("");
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  const loadSaved = () => api.get("/wellness/saved").then((r) => setSaved(r.data.items)).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const run = async () => {
    if (!topic.trim() && !nation && !plant) { toast.error("Describe a wellness topic to research."); return; }
    setLoading(true); setResult(null); setShowSaved(false);
    try {
      const { data } = await api.post("/indigenous-wellness/research", { topic, nation, region, plant, period, compare });
      setResult(data);
    } catch (e) { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const saveResearch = async () => {
    await api.post("/wellness/save", {
      topic: result.topic, nation: result.nation,
      plants: result.plants.map((p) => ({ common_name: p.common_name, scientific_name: p.scientific_name, recorded_association: p.recorded_association, source: p.source, safety: p.safety })),
      sources: result.sources, historical_context: result.historical_context,
    });
    toast.success("Saved to your plant library");
    loadSaved();
  };

  const delSaved = async (id) => { await api.delete(`/wellness/saved/${id}`); loadSaved(); toast.success("Removed"); };

  return (
    <div data-testid="wellness-assistant">
      {/* Search panel */}
      <Panel className="p-6 mb-5 bg-gradient-to-br from-[#123524] to-[#1c4a33] border-0">
        <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[#D4AF37]" /><h2 className="font-serif text-2xl font-bold text-white">Indigenous Wellness Research Assistant</h2></div>
        <p className="text-sm text-[#B9E4C9] mb-5">Explore documented traditional plant knowledge connected to Indigenous Nations and communities. This is an educational research tool — it does not diagnose or prescribe.</p>
        <label className="block text-xs font-bold uppercase tracking-wide text-[#D4AF37] mb-1">What would you like to research?</label>
        <textarea data-testid="wellness-topic-input" className="w-full rounded-lg border-0 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] mb-3" rows={2}
          placeholder="e.g. Traditional Cherokee plant knowledge associated with coughs" value={topic} onChange={(e) => setTopic(e.target.value)} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select className={field} value={nation} onChange={(e) => setNation(e.target.value)} data-testid="wellness-nation-filter"><option value="">All Nations</option>{(meta?.nations || []).map((n) => <option key={n} value={n}>{n}</option>)}</select>
          <select className={field} value={region} onChange={(e) => setRegion(e.target.value)} data-testid="wellness-region-filter"><option value="">All Regions</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select>
          <select className={field} value={plant} onChange={(e) => setPlant(e.target.value)} data-testid="wellness-plant-filter"><option value="">All Plants</option>{(plants || []).map((p) => <option key={p.id} value={p.id}>{p.common_name}</option>)}</select>
          <select className={field} value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="wellness-period-filter">{PERIODS.map((p, i) => <option key={p} value={i === 0 ? "" : p}>{p}</option>)}</select>
        </div>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <button onClick={run} disabled={loading} data-testid="wellness-explore-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#123524] px-6 py-3 text-sm font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} {loading ? "Researching documented records…" : "Explore Traditional Knowledge"}
          </button>
          <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer" data-testid="wellness-compare-toggle">
            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
            <GitCompare size={15} /> Compare across Nations
          </label>
          {saved.length > 0 && (
            <button onClick={() => { setShowSaved((v) => !v); setResult(null); }} data-testid="wellness-saved-toggle" className="ml-auto text-sm text-[#B9E4C9] hover:text-white underline">
              {showSaved ? "Hide saved research" : `My saved research (${saved.length})`}
            </button>
          )}
        </div>
      </Panel>

      <Disclaimer />

      {/* Saved research view */}
      {showSaved && (
        <div className="space-y-3" data-testid="wellness-saved-list">
          {saved.length === 0 ? <div className="text-center py-10 text-[#756D7E]">No saved research yet.</div> : saved.map((s) => (
            <Panel key={s.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-lg font-semibold text-[#180D24]">{s.topic || "Wellness research"}</div>
                  <div className="text-xs text-[#8F7220] font-mono">{s.nation ? s.nation + " · " : ""}{new Date(s.created_at).toLocaleDateString()} · {s.plants.length} plants</div>
                  <div className="text-sm text-[#756D7E] mt-1">{s.plants.map((p) => p.common_name).join(", ")}</div>
                </div>
                <button onClick={() => delSaved(s.id)} data-testid={`wellness-del-${s.id}`} className="text-[#B42318] hover:bg-[#FDECEC] rounded-full p-2"><Trash2 size={15} /></button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {/* Emergency */}
      {result?.emergency && (
        <Panel className="p-6 border-[#E5484D]/50 bg-[#FDECEC]" data-testid="wellness-emergency">
          <div className="flex items-center gap-2 text-[#B42318] font-bold text-lg mb-2"><Phone size={20} /> Please seek immediate help</div>
          <p className="text-[#7A271A]">{result.message}</p>
        </Panel>
      )}

      {/* Insufficient */}
      {result?.insufficient && (
        <Panel className="p-6" data-testid="wellness-insufficient"><p className="text-[#5a5460]">{result.insufficient}</p></Panel>
      )}

      {/* Results */}
      {result && !result.emergency && !result.insufficient && (
        <div data-testid="wellness-results">
          {result.personalization_notice && (
            <div className="rounded-xl border border-[#D4AF37]/50 bg-[#FCF8EE] p-4 mb-5" data-testid="wellness-personalization">
              <div className="flex items-center gap-2 text-[#8F7220] text-xs font-bold uppercase mb-1"><ShieldAlert size={14} /> A note before we continue</div>
              <p className="text-sm text-[#5a5460]">{result.personalization_notice}</p>
            </div>
          )}

          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#8F7220]">Your traditional knowledge research</div>
              <h2 className="font-serif text-2xl font-bold text-[#180D24]">{result.topic || "Documented plant knowledge"}</h2>
              {result.nation ? <div className="text-sm text-[#59288E] font-mono mt-0.5">{result.nation}</div> : null}
            </div>
            <button onClick={saveResearch} data-testid="wellness-save-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-4 py-2 text-sm font-bold hover:bg-[#482276] transition-colors"><BookmarkPlus size={15} /> Save to my plant library</button>
          </div>

          {/* Historical context (Claude) */}
          {result.historical_context && (
            <Panel className="p-5 mb-5" data-testid="wellness-context">
              <div className="flex items-center gap-2 text-[#59288E] text-xs font-bold uppercase mb-2"><ScrollText size={14} /> Historical context</div>
              <div className="text-sm text-[#1A171D] leading-relaxed whitespace-pre-line">{result.historical_context}</div>
            </Panel>
          )}

          {/* Documented plants */}
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-3">Documented plants</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{result.plants.map((p) => <PlantCard key={p.id} p={p} />)}</div>

          {/* Compare across nations */}
          {result.comparison?.length > 0 && (
            <div className="mt-6" data-testid="wellness-comparison">
              <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-1 flex items-center gap-2"><GitCompare size={18} /> Compare across Nations</h3>
              <p className="text-xs text-[#756D7E] mb-3">Different Nations documented different plants. Never assume all Indigenous Nations shared one medicinal system.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {result.comparison.map((c) => (
                  <Panel key={c.nation} className="p-4">
                    <div className="font-serif text-lg font-semibold text-[#2A1442]">{c.nation}</div>
                    <ul className="mt-2 space-y-1.5">{c.plants.map((p, i) => <li key={i} className="text-sm text-[#1A171D]"><span className="font-semibold">{p.common_name}</span> — <span className="text-[#756D7E]">{p.association}</span></li>)}</ul>
                  </Panel>
                ))}
              </div>
            </div>
          )}

          {/* Sources */}
          {result.sources?.length > 0 && (
            <div className="mt-6" data-testid="wellness-sources">
              <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-2">Sources</h3>
              <ul className="space-y-1.5">{result.sources.map((s, i) => <li key={i} className="text-sm text-[#5a5460] flex items-start gap-2"><Landmark size={14} className="text-[#8F7220] mt-0.5 shrink-0" /> {s.citation} <span className="text-[10px] font-bold uppercase text-[#59288E] bg-[#F3EEFA] px-1.5 py-0.5 rounded-full">{s.type}</span></li>)}</ul>
            </div>
          )}

          {/* Related content */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="wellness-related">
            <button onClick={() => onNavigate && onNavigate("tribal_recipes", {})} className="inline-flex items-center gap-1.5 rounded-full bg-[#FCF3E4] text-[#8F7220] px-3 py-1.5 text-xs font-semibold hover:bg-[#8F7220] hover:text-white transition-colors"><UtensilsCrossed size={13} /> Explore Recipes</button>
            <button onClick={() => onNavigate && onNavigate("language_lab", {})} className="inline-flex items-center gap-1.5 rounded-full bg-[#F3EEFA] text-[#59288E] px-3 py-1.5 text-xs font-semibold hover:bg-[#59288E] hover:text-white transition-colors"><Languages size={13} /> Learn Plant Names</button>
            <button onClick={() => onNavigate && onNavigate("treaties", result.nation ? { nation: result.nation } : {})} className="inline-flex items-center gap-1.5 rounded-full bg-[#E7F6EC] text-[#166534] px-3 py-1.5 text-xs font-semibold hover:bg-[#166534] hover:text-white transition-colors"><ScrollText size={13} /> Explore Treaties</button>
          </div>
        </div>
      )}
    </div>
  );
}
